import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Manual Hours Entry modal — used by both Admin and Partner dashboards.
 * onSaved() is invoked after a successful create so the parent can refresh lists.
 */
const ManualHoursModal = ({ isOpen, onClose, onSaved, scope = 'admin', partnerOrgId = null }) => {
  const [participants, setParticipants] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    user_email: '',
    opportunity_id: '',
    manual_label: '',
    service_date: new Date().toISOString().split('T')[0],
    credit_hours: 1,
    reason: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const usersUrl = scope === 'partner' ? `${API}/partner/participants` : `${API}/admin/users`;
      const [usersRes, oppsRes] = await Promise.all([
        axios.get(usersUrl).catch(() => ({ data: [] })),
        axios.get(`${API}/opportunities`).catch(() => ({ data: [] })),
      ]);
      const raw = usersRes.data || [];
      // Normalize: admin/users returns user objs; partner/participants returns objs with email + name
      const users = raw
        .map(u => ({ email: u.email, name: u.name || u.email, role: u.role || 'user' }))
        .filter(u => u.email && u.role === 'user');
      // Dedup by email
      const seen = new Set();
      setParticipants(users.filter(u => (seen.has(u.email) ? false : (seen.add(u.email), true))));
      const opps = (oppsRes.data || []).filter(o =>
        scope !== 'partner' || (o.partner_org_id === partnerOrgId)
      );
      setOpportunities(opps);
    } catch (e) {
      // best-effort
    } finally {
      setLoading(false);
    }
  }, [scope, partnerOrgId]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setForm({
        user_email: '',
        opportunity_id: '',
        manual_label: '',
        service_date: new Date().toISOString().split('T')[0],
        credit_hours: 1,
        reason: '',
      });
    }
  }, [isOpen, fetchData]);

  const submit = async (confirmDuplicate = false) => {
    if (!form.user_email) {
      toast.error('Please pick a participant');
      return;
    }
    if (!form.opportunity_id && !form.manual_label.trim()) {
      toast.error('Pick an opportunity OR enter a manual label');
      return;
    }
    if (!form.reason.trim()) {
      toast.error('A reason/note is required');
      return;
    }
    if (!form.credit_hours || form.credit_hours <= 0) {
      toast.error('Hours must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        user_email: form.user_email,
        service_date: form.service_date,
        credit_hours: parseFloat(form.credit_hours),
        reason: form.reason.trim(),
        confirm_duplicate: confirmDuplicate,
      };
      if (form.opportunity_id) {
        payload.opportunity_id = form.opportunity_id;
      } else {
        payload.manual_label = form.manual_label.trim();
      }
      await axios.post(`${API}/admin/hours/manual`, payload);
      toast.success('Manual hours added');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Failed to save';
      if (status === 409) {
        if (window.confirm(`${detail}\n\nDo you want to add this entry anyway?`)) {
          await submit(true);
          return;
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="manual-hours-modal">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E9DDCF] flex items-start justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-2xl font-bold text-[#222222]">Add Manual Hours</h2>
            <p className="text-sm text-[#666] mt-1">Use this when a participant's hours need to be credited outside the normal flow (app glitch, missed check-out, off-platform work).</p>
          </div>
          <button onClick={onClose} className="text-[#666] hover:text-[#222]" data-testid="close-manual-hours-modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>This will be recorded as a <strong>Manual Entry</strong> with your name as the entered_by. It counts toward the participant's total and all reports.</span>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Participant *</label>
            <select
              className="input-field"
              value={form.user_email}
              onChange={(e) => setForm(f => ({ ...f, user_email: e.target.value }))}
              data-testid="manual-hours-participant"
              disabled={loading}
            >
              <option value="">— Pick a participant —</option>
              {participants.map(u => (
                <option key={u.email} value={u.email}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Opportunity (optional)</label>
            <select
              className="input-field"
              value={form.opportunity_id}
              onChange={(e) => setForm(f => ({ ...f, opportunity_id: e.target.value }))}
              data-testid="manual-hours-opportunity"
              disabled={loading}
            >
              <option value="">— Other / Manual entry —</option>
              {opportunities.map(o => (
                <option key={o.id} value={o.id}>
                  {o.title} ({o.date})
                </option>
              ))}
            </select>
          </div>

          {!form.opportunity_id && (
            <div>
              <label className="block text-sm font-medium mb-1">Manual label *</label>
              <input
                type="text"
                className="input-field"
                value={form.manual_label}
                onChange={(e) => setForm(f => ({ ...f, manual_label: e.target.value }))}
                placeholder="e.g., Helped with food drive (off-platform)"
                data-testid="manual-hours-label"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date of service *</label>
              <input
                type="date"
                className="input-field"
                value={form.service_date}
                onChange={(e) => setForm(f => ({ ...f, service_date: e.target.value }))}
                data-testid="manual-hours-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hours credited *</label>
              <input
                type="number"
                step="0.25"
                min="0.25"
                className="input-field"
                value={form.credit_hours}
                onChange={(e) => setForm(f => ({ ...f, credit_hours: e.target.value }))}
                data-testid="manual-hours-hours"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Reason / note *</label>
            <textarea
              className="input-field"
              rows={3}
              value={form.reason}
              onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="e.g., App glitch — missed check-out at 3 PM. Verified with supervisor."
              data-testid="manual-hours-reason"
            />
          </div>
        </div>

        <div className="p-6 border-t border-[#E9DDCF] flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-outline" data-testid="cancel-manual-hours">Cancel</button>
          <button
            onClick={() => submit(false)}
            disabled={submitting}
            className="btn-primary"
            data-testid="submit-manual-hours"
          >
            {submitting ? 'Saving…' : 'Add Hours'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualHoursModal;
